"use client";

import React from "react";
import { ShieldCheck, Building2, HardHat, FileCheck2, ClipboardList, CircleCheck as CheckCircle2, ArrowRight, MapPin } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const accessContexts = [{
  title: "Town-centre retail & commercial",
  description: "Pedestrian protection gantries, pavement hoarding, and phased erection out-of-hours to keep high-street foot traffic moving safely.",
  tag: "High-street access"
}, {
  title: "High-density residential",
  description: "Tied scaffolding with debris netting and secure ground access for occupied apartment buildings and multi-storey developments.",
  tag: "Multi-occupancy"
}, {
  title: "Office refurbishments",
  description: "Internal crash decks, lift-shaft towers, and cantilevered access designed around active tenants and restricted loading bays.",
  tag: "Live workplaces"
}];
const safetySteps = [{
  icon: ClipboardList,
  step: "01",
  title: "Site assessment & design",
  description: "Evaluating pavement licenses, ground loadings, and live public interfaces before tube touches the ground."
}, {
  icon: HardHat,
  step: "02",
  title: "Safe erection & adaptation",
  description: "Trained CISRS operatives working strictly to TG20:21 compliance guidelines with fall protection throughout."
}, {
  icon: FileCheck2,
  step: "03",
  title: "Handover inspection",
  description: "Formal statutory handover certificates and visible Scafftag issue prior to first trade use."
}];
const nearbyAreas = [{
  name: "Guildford",
  to: "/scaffolding-in-guildford"
}, {
  name: "Camberley",
  to: "/scaffolding-in-camberley"
}, {
  name: "Weybridge",
  to: "/scaffolding-in-weybridge"
}];
export default function WokingSafety() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="wokingaccesssafety" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <motion.div initial={{
        opacity: shouldReduceMotion ? 1 : 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="max-w-3xl mb-14">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Badge variant="outline" className="text-foreground">
              Woking & Surrey Operations
            </Badge>
            <Badge variant="outline" className="text-muted-foreground border-border flex items-center gap-1">
              <MapPin className="size-3 text-primary" />
              <span>Postcodes GU21 & GU22</span>
            </Badge>
            <Badge variant="secondary" className="font-semibold">
              NASC & CHAS Accredited
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Access and safety standards for Woking projects
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Delivering safe, fully compliant scaffolding across the GU21 and GU22 postcode districts. From busy town-centre retail frontages to occupied residential blocks, we design and erect scaffolds that keep your workforce safe and public thoroughfares clear.
          </p>
        </motion.div>

        {/* Woking Context Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {accessContexts.map((item, index) => <motion.div key={item.title} variants={itemVariants} data-index={index}>
              <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                <CardHeader>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                      {item.tag}
                    </span>
                    <Building2 className="size-4 text-muted-foreground" />
                  </div>
                  <CardTitle className="text-xl font-semibold text-card-foreground">
                    {item.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Safety Protocol Banner */}
        <div className="bg-card border border-border rounded-xl p-6 md:p-10 shadow-sm mb-16">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 text-primary font-medium text-sm mb-1">
                <ShieldCheck className="size-4" />
                <span>Verified Compliance</span>
              </div>
              <h3 className="text-2xl font-bold text-card-foreground">
                Three-step site assurance process
              </h3>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted text-xs font-semibold text-foreground border border-border">
                <CheckCircle2 className="size-3.5 text-chart-4" />
                <span>NASC Member</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted text-xs font-semibold text-foreground border border-border">
                <CheckCircle2 className="size-3.5 text-chart-4" />
                <span>CHAS Premium</span>
              </div>
            </div>
          </div>

          <Separator className="mb-8" />

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {safetySteps.map((step, index) => {
            const IconComp = step.icon;
            return <motion.div key={step.step} variants={itemVariants} data-index={index} className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                      <IconComp className="size-5" />
                    </div>
                    <span className="font-mono text-2xl font-bold text-muted-foreground/40">
                      {step.step}
                    </span>
                  </div>
                  <h4 className="text-lg font-semibold text-card-foreground mt-2">
                    {step.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Footer Actions & Nearby Coverage */}
        <div className="rounded-lg bg-muted border border-border p-6 space-y-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-foreground text-center sm:text-left font-medium">
              Planning a refurbishment or commercial build in Woking (GU21 & GU22) or surrounding Surrey districts?
            </p>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <Button asChild variant="default" size="default">
                <Link to="/ContactRequestQuotePage">
                  Request a quote
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="default">
                <Link to="/HealthSafetyAccreditationsPage">
                  View accreditations
                </Link>
              </Button>
            </div>
          </div>

          {/* Nearby Coverage Navigation */}
          <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs sm:text-sm">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
              <span className="font-semibold text-foreground">Nearby areas:</span>
              {nearbyAreas.map((area, i) => <React.Fragment key={area.to}>
                  <Link to={area.to} data-index={i} className="text-primary hover:underline font-medium">
                    {area.name}
                  </Link>
                  {i < nearbyAreas.length - 1 && <span className="text-border" aria-hidden="true">•</span>}
                </React.Fragment>)}
            </div>
            <Link to="#" className="inline-flex items-center gap-1 font-medium text-foreground hover:text-primary transition-colors shrink-0">
              <span>View all areas</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </div>
      </div>
    </section>;
}